import { FreeAccountSignup } from "@ecologi/react-components/src/FreeAccountSignup";
import { FreeSignupFormValues } from "@ecologi/react-components/src/FreeAccountSignup/FreeSignupForm";
import { AccountTypeEnum, hasQueryInitiated, User } from "@ecologi/shared";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { useRecaptcha } from "@ecologi/react-components";
import { Head } from "../../../components";
import { APIError, formatAPIErrorForView } from "../../../lib";
import {
  HubspotField,
  hubspotPayAsYouGoForm,
} from "../../../lib/api/hubspot-pay-as-you-go-form";
import { createFreeAccount } from "../../../lib/api/subscriptions";
import { useStore } from "../../../store";
import { CAPTCHA_ACTIONS } from "@ecologi/shared";
import { fetchFromAPI } from "../../../lib/api/fetch-from-api";
import { getAuth0SessionData } from "../../../lib/api/user";
import {
  clearAuthenticatedSignupPending,
  hasAuthenticatedSignupPending,
  setAuthenticatedSignupPending,
} from "../authenticated-signup-return-to";
import { AuthenticatedSignupGate } from "../authenticated-signup-gate";

const customPropertyNames = {
  lastName: "lastname",
  firstName: "firstname",
  groupName: "company",
} as const;

export function FreePlansPage() {
  const router = useRouter();
  const recaptcha = useRecaptcha();
  const hasStartedProfileRedirect = useRef(false);
  const { user, setUser, isCheckingUserSession, location, referrerUserId } =
    useStore();

  const { data: auth0Session, isLoading: isCheckingAuth0Session } = useQuery(
    "auth0-session",
    getAuth0SessionData,
    { retry: false }
  );

  const hasPendingAuthenticatedSignup = hasAuthenticatedSignupPending();

  // The 'parent' parameter indicates the profile being created is for a sub-forest.
  // The 'type' parameter controls the signup form state; business (default) or individual.
  const { parent, type: accountTypeParam } = router.query;
  const parentUsername = Array.isArray(parent) ? parent[0] : parent;

  const accountType = Array.isArray(accountTypeParam)
    ? accountTypeParam[0]
    : accountTypeParam;

  const defaultAccountType =
    accountType === AccountTypeEnum.INDIVIDUAL
      ? AccountTypeEnum.INDIVIDUAL
      : AccountTypeEnum.BUSINESS;

  const { data: parentExists } = useQuery(
    ["parentProfile", parentUsername],
    async () => {
      try {
        await fetchFromAPI(`/users/${parentUsername}/profile`);
        return true;
      } catch (error) {
        if (error instanceof APIError && APIError.isNotFoundError(error)) {
          return false;
        }
        return null;
      }
    },
    { enabled: !!parentUsername }
  );

  const hubspotPayAsYouGoBodyFormatter = (
    formValues: FreeSignupFormValues,
    auth0Email: string
  ): HubspotField[] => {
    const fields = Object.keys(formValues).reduce(
      (acc: HubspotField[], key: string) => {
        acc.push({
          name: customPropertyNames[key] ?? key,
          value: formValues[key],
        });
        return acc;
      },
      []
    );

    fields.push({ name: "email", value: auth0Email });

    return fields;
  };

  const [createAccountMutation, createAccountResult] = useMutation(
    async (data: FreeSignupFormValues) => {
      if (!auth0Session) {
        setAuthenticatedSignupPending();

        window.location.assign(
          `/auth/login?screen_hint=signup&returnTo=${encodeURIComponent(
            router.asPath
          )}`
        );

        return new Promise<never>(() => {});
      }

      const token = await recaptcha.execute(CAPTCHA_ACTIONS.PAYG);
      const isBusiness = data.accountType === AccountTypeEnum.BUSINESS;
      const user = await createFreeAccount({
        type: "pay-as-you-go",
        captchaToken: token,
        customer: {
          emailAddress: "",
          firstName: data.firstName,
          lastName: data.lastName,
          location: data.country,
        },
        user: {
          affiliateClickId: undefined,
          groupName: isBusiness ? data.groupName : "",
          isMarketingAccepted: false,
          parent: parentUsername,
          referralSource: undefined,
          referrerUserId,
          accountType: data.accountType,
          username: data.username,
        },
      });

      if (auth0Session.email) {
        hubspotPayAsYouGoForm({
          fields: hubspotPayAsYouGoBodyFormatter(data, auth0Session.email),
        });
      }

      return user;
    },
    {
      onError: (error: APIError | Error) => {
        toast.error(
          error instanceof APIError
            ? formatAPIErrorForView(error)
            : error.message || "An unexpected error occurred. Please try again."
        );
      },
      onSuccess: ({ user }) => {
        clearAuthenticatedSignupPending();
        setUser(new User(user), { isCheckingUserSession: false });
        router.push("/thanks");
      },
    }
  );

  const hasSignupInitiated = hasQueryInitiated(createAccountResult);

  const shouldRedirectExistingUserToProfile = user && !hasSignupInitiated;

  useEffect(() => {
    if (
      !shouldRedirectExistingUserToProfile ||
      hasStartedProfileRedirect.current
    ) {
      return;
    }

    hasStartedProfileRedirect.current = true;
    clearAuthenticatedSignupPending();
    router.replace("/profile");
  }, [router, shouldRedirectExistingUserToProfile]);

  // Passing 'isLoading' as true even when the query has succeeded ensures the form
  // doesn't unlock in the brief moment between the mutation succeeding and the user
  // being sent to the thanks page
  const isLoading =
    createAccountResult.isLoading || createAccountResult.isSuccess;

  const isCheckingPendingAuthenticatedSignupUser =
    hasPendingAuthenticatedSignup && auth0Session && isCheckingUserSession;

  if (shouldRedirectExistingUserToProfile) {
    return <Head title="Complete sign up" />;
  }

  return (
    <>
      <Head title="Complete sign up" />
      <FreeAccountSignup
        hasSignupInitiated={hasSignupInitiated}
        isCheckingUserSession={isCheckingUserSession}
        user={user}
        parentUsername={parentUsername}
        parentExists={parentExists ?? null}
      >
        {isCheckingPendingAuthenticatedSignupUser ? null : (
          <AuthenticatedSignupGate>
            {({ isCheckingAuth0Session }) => (
              <FreeAccountSignup.Form
                createFreeAccount={createAccountMutation}
                location={location}
                isLoading={isLoading || isCheckingAuth0Session}
                defaultAccountType={defaultAccountType}
              />
            )}
          </AuthenticatedSignupGate>
        )}
      </FreeAccountSignup>
    </>
  );
}
